import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Textarea from '../ui/Textarea';
import FieldAnswerList from '../fields/FieldAnswerList';
import { formatAnswer } from './StepTimeline';
import { groupFieldsBySection } from '../../utils/fieldSections';
import { requestStepEdit, approveStepEdit, rejectStepEdit } from '../../api/candidates';
import { pushToast } from '../ui/toastBus';
import { usePermission } from '../../auth/usePermission';
import { formatValidationErrors } from '../../utils/errors';

/**
 * Shared "propose a change to locked-in data, get it approved" UI. Two scopes share it:
 *  - a whole completed TASK step (sectionId omitted) — checklist, all fields and remarks.
 *  - one already-saved field section on a step still in progress (sectionId given) — just
 *    that section's fields; the step itself isn't done yet, so checklist/remarks aren't locked.
 * Nothing changes on the real step until a user with the approve permission reviews the
 * request (which may be the same person who requested it, if they hold that permission too)
 * — see WorkflowEngine::requestStepEdit()/requestSectionEdit()/approveStepEdit().
 */

const FIELDS_GRID = 'grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2';

/**
 * Whole-step scope only: fields grouped under their section headings, same layout as the live
 * processing form — and, when `onUpdateSection` is given, each section gets its own button to
 * request an edit for just that section instead of the whole step.
 */
function FieldsBySection({ fields, sections, answers, onAnswer, onUpdateSection, savingSectionId }) {
  const bySection = groupFieldsBySection(fields);
  const ungrouped = bySection.get(null) || [];

  return (
    <div className="space-y-3">
      {sections.map((section) => {
        const sectionFields = bySection.get(section.id) || [];
        if (sectionFields.length === 0) return null;
        return (
          <div key={section.id} className="rounded-md border border-gray-200 p-3">
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-gray-600">{section.title}</p>
              {onUpdateSection && (
                <Button
                  variant="secondary"
                  className="px-3 py-1.5 text-xs"
                  onClick={() => onUpdateSection(section, sectionFields)}
                  loading={savingSectionId === section.id}
                >
                  Update section
                </Button>
              )}
            </div>
            <FieldAnswerList fields={sectionFields} answers={answers} onAnswer={onAnswer} className={FIELDS_GRID} />
          </div>
        );
      })}
      {ungrouped.length > 0 && (
        <div>
          {sections.length > 0 && <p className="mb-1.5 text-xs font-medium text-gray-600">Other fields</p>}
          <FieldAnswerList fields={ungrouped} answers={answers} onAnswer={onAnswer} className={FIELDS_GRID} />
        </div>
      )}
    </div>
  );
}

export function EditRequestModal({ open, onClose, candidate, step, sectionId, sectionTitle, fields, sections = [] }) {
  const queryClient = useQueryClient();
  const isSection = !!sectionId;
  const [checklistState, setChecklistState] = useState(step.checklist_state || {});
  const [answers, setAnswers] = useState(step.answers || {});
  const [remarks, setRemarks] = useState(step.remarks || '');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });

  const mutation = useMutation({
    mutationFn: () =>
      requestStepEdit(candidate.id, step.candidate_step_id, {
        section_id: sectionId || undefined,
        // A section-scoped request only ever carries that section's own answers — the approval
        // merges them in rather than replacing the step's whole answer set.
        answers: isSection ? Object.fromEntries(fields.map((f) => [f.id, answers[f.id]])) : answers,
        checklist_state: isSection ? undefined : checklistState,
        remarks: isSection ? undefined : remarks,
        request_note: note.trim() || null,
      }),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Edit requested — awaiting approval.' });
      invalidate();
      onClose();
    },
    onError: (err) => {
      const errors = err.response?.data?.errors;
      setError((errors && formatValidationErrors(errors)) || err.response?.data?.message || 'Could not request edit.');
    },
  });

  // Whole-step scope only: a dedicated "Update section" click on one of the grouped sections,
  // independent of the general Submit button below (which only ever covers checklist/remarks/
  // ungrouped fields once sections have their own buttons).
  const sectionMutation = useMutation({
    mutationFn: ({ section, sectionFields }) =>
      requestStepEdit(candidate.id, step.candidate_step_id, {
        section_id: section.id,
        answers: Object.fromEntries(sectionFields.map((f) => [f.id, answers[f.id]])),
      }),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Edit requested — awaiting approval.' });
      invalidate();
      onClose();
    },
    onError: (err) => {
      const errors = err.response?.data?.errors;
      setError((errors && formatValidationErrors(errors)) || err.response?.data?.message || 'Could not request edit.');
    },
  });

  const toggleChecklistItem = (item) => setChecklistState((prev) => ({ ...prev, [item]: !prev[item] }));
  const setAnswer = (fieldId, value) => setAnswers((prev) => ({ ...prev, [fieldId]: value }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Request edit: ${step.title}${sectionTitle ? ` — ${sectionTitle}` : ''}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>
            {!isSection && sections.length > 0 ? 'Submit other changes' : 'Submit for approval'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {isSection
            ? 'This section is already saved. Your changes won\'t take effect until someone with approval permission reviews and approves them.'
            : sections.length > 0
              ? "This step is already completed. Each section below has its own \"Update section\" button to request an edit for just that section; use \"Submit other changes\" for checklist, remarks, or fields outside a section. Only one edit request can be pending at a time."
              : "This step is already completed. Your changes won't take effect until someone with approval permission reviews and approves them."}
        </p>

        {!isSection && step.checklist?.length > 0 && (
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Checklist</h4>
            <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
              {step.checklist.map((item) => (
                <label key={item} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="accent-primary" checked={!!checklistState[item]} onChange={() => toggleChecklistItem(item)} />
                  {item}
                </label>
              ))}
            </div>
          </div>
        )}

        {fields.length > 0 && (
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{sectionTitle || 'Fields'}</h4>
            {!isSection && sections.length > 0 ? (
              <FieldsBySection
                fields={fields}
                sections={sections}
                answers={answers}
                onAnswer={setAnswer}
                onUpdateSection={(section, sectionFields) => sectionMutation.mutate({ section, sectionFields })}
                savingSectionId={sectionMutation.isPending ? sectionMutation.variables?.section?.id : null}
              />
            ) : (
              <FieldAnswerList fields={fields} answers={answers} onAnswer={setAnswer} className={FIELDS_GRID} />
            )}
          </div>
        )}

        {!isSection && <Textarea label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />}
        <Textarea label="Note to the approver (optional)" value={note} onChange={(e) => setNote(e.target.value)} />

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}

function ProposedChanges({ step, request, fields }) {
  const isSection = !!request.section_id;
  const rows = [
    ...(!isSection
      ? (step.checklist || []).map((item) => ({
          label: item,
          before: step.checklist_state?.[item] ? 'Checked' : 'Unchecked',
          after: request.proposed_checklist_state?.[item] ? 'Checked' : 'Unchecked',
        }))
      : []),
    ...fields.map((f) => ({
      label: f.label,
      before: formatAnswer(step.answers?.[f.id]),
      after: formatAnswer(request.proposed_answers?.[f.id]),
    })),
    ...(!isSection ? [{ label: 'Remarks', before: step.remarks || '—', after: request.proposed_remarks || '—' }] : []),
  ].filter((row) => row.before !== row.after);

  if (rows.length === 0) {
    return <p className="text-xs text-gray-500">No changes from the current values.</p>;
  }

  return (
    <div className="space-y-1.5">
      {rows.map((row) => (
        <div key={row.label} className="text-xs">
          <span className="font-medium text-gray-700">{row.label}: </span>
          <span className="text-gray-400 line-through">{row.before}</span>
          <span className="mx-1 text-gray-400">→</span>
          <span className="text-gray-800">{row.after}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Self-sufficient: reads `step.pending_edit_request` itself and figures out from its
 * `section_id` whether it's a section- or whole-step-scoped request, so it can be dropped in
 * once per step regardless of which kind of edit is pending.
 */
export function PendingEditBanner({ candidate, step, onResolved }) {
  const queryClient = useQueryClient();
  const { can } = usePermission();
  const request = step.pending_edit_request;
  const [rejecting, setRejecting] = useState(false);
  const [reviewRemarks, setReviewRemarks] = useState('');
  const [showChanges, setShowChanges] = useState(false);

  // Hooks below must run unconditionally regardless of whether there's a pending request — the
  // early return happens later, right before the JSX.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });
    onResolved?.();
  };

  const approveMutation = useMutation({
    mutationFn: () => approveStepEdit(candidate.id, step.candidate_step_id, request?.id),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Edit approved.' });
      invalidate();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not approve edit.' }),
  });

  const rejectMutation = useMutation({
    mutationFn: () => rejectStepEdit(candidate.id, step.candidate_step_id, request?.id, reviewRemarks.trim() || null),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Edit rejected.' });
      setRejecting(false);
      invalidate();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not reject edit.' }),
  });

  if (!request) return null;

  const section = request.section_id ? (step.field_sections || []).find((s) => s.id === request.section_id) : null;
  const fields = request.section_id ? groupFieldsBySection(step.fields || []).get(request.section_id) || [] : step.fields || [];
  const canApprove = can('approve edit candidate completed step');

  return (
    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-amber-900">
          <span className="font-medium">Edit requested{section ? ` for "${section.title}"` : ''}</span> by {request.requested_by} on{' '}
          {new Date(request.requested_at).toLocaleString()}
          {request.request_note && <span className="block text-xs text-amber-800">"{request.request_note}"</span>}
        </p>
        <button type="button" onClick={() => setShowChanges((v) => !v)} className="text-xs text-amber-800 hover:underline">
          {showChanges ? 'Hide changes' : 'View changes'}
        </button>
      </div>

      {showChanges && (
        <div className="mt-2 rounded-md bg-white/70 p-2">
          <ProposedChanges step={step} request={request} fields={fields} />
        </div>
      )}

      {canApprove ? (
        <div className="mt-3">
          {rejecting ? (
            <div className="space-y-2">
              <Textarea
                placeholder="Reason for rejecting (optional)"
                value={reviewRemarks}
                onChange={(e) => setReviewRemarks(e.target.value)}
                className="bg-white"
              />
              <div className="flex gap-2">
                <Button variant="danger" onClick={() => rejectMutation.mutate()} loading={rejectMutation.isPending}>
                  Confirm reject
                </Button>
                <Button variant="secondary" onClick={() => setRejecting(false)} disabled={rejectMutation.isPending}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button onClick={() => approveMutation.mutate()} loading={approveMutation.isPending}>
                Approve
              </Button>
              <Button variant="danger" onClick={() => setRejecting(true)} disabled={approveMutation.isPending}>
                Reject
              </Button>
            </div>
          )}
        </div>
      ) : (
        <p className="mt-2 text-xs text-amber-700">Awaiting approval from someone with permission to approve edits.</p>
      )}
    </div>
  );
}
